import getAdminAuthorization from './getAdminAuthorization'
import moduleIsActive from './moduleIsActive'
import {
  ADMIN_MENU_ITEMS,
  AGENT_MENU_PAGES,
  FAMILY_LABEL_KEYS,
  PAGE_LABEL_KEYS,
  VERTICAL_ICON_KEYS,
  VERTICAL_LABEL_KEYS,
  VERTICALS_FAMILY,
} from '../redesignedMenu'
import type { MenuAdminItemConfig, MenuPageKey, MenuVertical } from '../redesignedMenu'

export interface RedesignedMenuContext {
  routes: Record<string, string>
  modulesStatus: Record<string, unknown> | null
  country: string
  isAdmin: boolean
  isSelectraAdmin: boolean
  isExternal: boolean
  commonData: Record<string, unknown>
  availableVerticals: string[]
  currentVertical: string
  error: boolean
}

export interface ResolvedMenuLink {
  kind: 'link'
  id: string
  to: string
  labelKey: string
  icon: string
}

export interface ResolvedMenuFamilyItem {
  id: string
  to: string
  labelKey: string
  icon: string
  current: boolean
}

export interface ResolvedMenuFamily {
  kind: 'family'
  id: string
  labelKey: string
  icon: string
  items: ResolvedMenuFamilyItem[]
}

export type ResolvedMenuEntry = ResolvedMenuLink | ResolvedMenuFamily

export type MenuSectionKey = 'agent' | 'admin' | 'verticals'

export interface ResolvedMenuSection {
  key: MenuSectionKey
  entries: ResolvedMenuEntry[]
}

export interface ResolvedMenu {
  sections: ResolvedMenuSection[]
  showLogout: boolean
}

const isPageVisible = (page: MenuPageKey | 'logout', context: RedesignedMenuContext) =>
  context.routes[page] !== undefined &&
  moduleIsActive(page, context.modulesStatus, context.country, context.isExternal, context.commonData) &&
  getAdminAuthorization(context.isAdmin, context.isSelectraAdmin, page) === true

const toLink = (page: MenuPageKey, context: RedesignedMenuContext): ResolvedMenuLink => ({
  kind: 'link',
  id: page,
  to: context.routes[page],
  labelKey: PAGE_LABEL_KEYS[page],
  icon: page,
})

const toAdminEntry = (item: MenuAdminItemConfig, context: RedesignedMenuContext): ResolvedMenuEntry | null => {
  if (typeof item === 'string') return isPageVisible(item, context) ? toLink(item, context) : null

  const pages = item.pages.filter(page => isPageVisible(page, context))
  if (pages.length === 0) return null
  if (pages.length === 1) return toLink(pages[0], context)

  return {
    kind: 'family',
    id: item.family,
    labelKey: FAMILY_LABEL_KEYS[item.family],
    icon: item.icon,
    items: pages.map(page => ({ ...toLink(page, context), current: false })),
  }
}

const resolveVerticalsFamily = (context: RedesignedMenuContext): ResolvedMenuFamily | null => {
  if (!context.isAdmin || context.availableVerticals.length < 2) return null

  return {
    kind: 'family',
    id: VERTICALS_FAMILY.id,
    labelKey: VERTICALS_FAMILY.labelKey,
    icon: VERTICALS_FAMILY.icon,
    items: (context.availableVerticals as MenuVertical[]).map(vertical => ({
      id: `to-${vertical}`,
      to: `/${vertical}`,
      labelKey: VERTICAL_LABEL_KEYS[vertical],
      icon: VERTICAL_ICON_KEYS[vertical],
      current: vertical === context.currentVertical,
    })),
  }
}

export const resolveRedesignedMenu = (context: RedesignedMenuContext): ResolvedMenu => {
  const agentEntries = context.error
    ? []
    : AGENT_MENU_PAGES.filter(page => isPageVisible(page, context)).map(page => toLink(page, context))
  const adminEntries = context.error
    ? []
    : ADMIN_MENU_ITEMS.map(item => toAdminEntry(item, context)).filter(
        (entry): entry is ResolvedMenuEntry => entry !== null,
      )
  const verticalsFamily = resolveVerticalsFamily(context)

  const sections: ResolvedMenuSection[] = [
    { key: 'agent', entries: agentEntries },
    { key: 'admin', entries: adminEntries },
    { key: 'verticals', entries: verticalsFamily ? [verticalsFamily] : [] },
  ]

  return {
    sections: sections.filter(section => section.entries.length > 0),
    showLogout: isPageVisible('logout', context),
  }
}
